/**
 * Step2MapAdapter - Adaptador para MapViewer
 * 
 * Convierte Infraestructura[] (Step2) a NormalizedInfrastructure[] (MapViewer).
 * Filtra automáticamente registros sin coordenadas válidas.
 * 
 * @module components/Step2/Step2MapAdapter
 * @version V08.1
 */

import type { Infraestructura, TypologyKey } from './types';
import { InfrastructureType, type NormalizedInfrastructure } from '../../types/infrastructure';

// =============================================================================
// MAPEO DE TIPOLOGÍAS
// =============================================================================

/**
 * Mapeo de TypologyKey (Step2) a InfrastructureType (MapViewer)
 * 
 * InfrastructureType usa los mismos valores string como enum values,
 * pero necesitamos mapeo explícito para tipos adicionales.
 */
const TYPOLOGY_TO_INFRASTRUCTURE: Record<TypologyKey, InfrastructureType> = {
  SANITARIO: InfrastructureType.HEALTH,
  EDUCATIVO: InfrastructureType.EDUCATION,
  POLICIAL: InfrastructureType.POLICE,
  CULTURAL: InfrastructureType.CULTURAL,
  RELIGIOSO: InfrastructureType.RELIGIOUS,
  EMERGENCIAS: InfrastructureType.EMERGENCY,
  DEPORTIVO: InfrastructureType.SPORTS,
  ADMINISTRATIVO: InfrastructureType.MUNICIPAL,
  VIAL: InfrastructureType.VIAL,
  ENERGETICO: InfrastructureType.ENERGY,
  HIDRAULICO: InfrastructureType.HYDRAULIC,
  OTRO: InfrastructureType.GENERIC,
};

// =============================================================================
// FUNCIONES DE CONVERSIÓN
// =============================================================================

/**
 * Convierte una Infraestructura de Step2 a NormalizedInfrastructure del visor.
 * 
 * @param item - Infraestructura de Step2
 * @returns NormalizedInfrastructure o null si no tiene coordenadas válidas
 * 
 * @example
 * ```typescript
 * const infra = { id: 1, nombre: 'Hospital', x: 452000, y: 4145000, ... };
 * const normalized = toNormalizedInfrastructure(infra);
 * // { id: '1', name: 'Hospital', x: 452000, y: 4145000, ... }
 * ```
 */
export function toNormalizedInfrastructure(
  item: Infraestructura
): NormalizedInfrastructure | null {
  // Filtrar registros sin coordenadas válidas
  if (item.x === null || item.y === null) {
    return null;
  }

  // Validar que las coordenadas estén en rango UTM30 Andalucía
  if (!isValidUTM30Coordinate(item.x, item.y)) {
    return null;
  }

  return {
    id: String(item.id),
    name: item.nombre,
    type: TYPOLOGY_TO_INFRASTRUCTURE[item.tipo] ?? InfrastructureType.GENERIC,
    x: item.x,
    y: item.y,
    score: item.score,
    address: item.direccion || undefined,
    municipality: item.municipio || undefined,
    province: item.provincia || undefined,
    muniCode: item.codIne || undefined,
    source: item.source || undefined,
    phone: item.telefono || undefined,
    metadata: item.observaciones ? { observaciones: item.observaciones } : undefined,
  };
}

/**
 * Convierte array de Infraestructura a NormalizedInfrastructure[].
 * Filtra automáticamente los registros sin coordenadas válidas.
 * 
 * @param infraestructuras - Array de infraestructuras de Step2
 * @returns Array de infraestructuras normalizadas (solo con coordenadas)
 * 
 * @example
 * ```typescript
 * const data = [
 *   { id: 1, x: 452000, y: 4145000, ... },
 *   { id: 2, x: null, y: null, ... },  // Se filtra
 * ];
 * const normalized = adaptForMapViewer(data);
 * // [{ id: '1', ... }]  - Solo 1 elemento
 * ```
 */
export function adaptForMapViewer(
  infraestructuras: Infraestructura[]
): NormalizedInfrastructure[] {
  return infraestructuras
    .map(toNormalizedInfrastructure)
    .filter((item): item is NormalizedInfrastructure => item !== null);
}

// =============================================================================
// ESTADÍSTICAS
// =============================================================================

/**
 * Estadísticas de adaptación para mostrar cobertura
 */
export interface AdaptationStats {
  /** Total de infraestructuras */
  total: number;
  /** Con coordenadas válidas */
  withCoords: number;
  /** Sin coordenadas */
  withoutCoords: number;
  /** Porcentaje de cobertura (0-100) */
  coverage: number;
}

/**
 * Calcula estadísticas de adaptación.
 * 
 * @param infraestructuras - Array de infraestructuras de Step2
 * @returns Estadísticas de cobertura
 * 
 * @example
 * ```typescript
 * const stats = getAdaptationStats(data);
 * // { total: 10, withCoords: 8, withoutCoords: 2, coverage: 80 }
 * ```
 */
export function getAdaptationStats(
  infraestructuras: Infraestructura[]
): AdaptationStats {
  const withCoords = infraestructuras.filter(
    (i) => i.x !== null && i.y !== null && isValidUTM30Coordinate(i.x, i.y)
  ).length;

  const total = infraestructuras.length;

  return {
    total,
    withCoords,
    withoutCoords: total - withCoords,
    coverage: total > 0 ? Math.round((withCoords / total) * 100) : 0,
  };
}

// =============================================================================
// VALIDACIÓN
// =============================================================================

/**
 * Rangos válidos para coordenadas UTM30 en Andalucía.
 * X: 100,000 - 700,000 (metros Este)
 * Y: 4,000,000 - 4,350,000 (metros Norte)
 */
const UTM30_BOUNDS = {
  minX: 100_000,
  maxX: 700_000,
  minY: 4_000_000,
  maxY: 4_350_000,
} as const;

/**
 * Verifica si las coordenadas están en el rango válido para UTM30 Andalucía.
 * 
 * @param x - Coordenada X (Este)
 * @param y - Coordenada Y (Norte)
 * @returns true si las coordenadas son válidas
 */
export function isValidUTM30Coordinate(x: number, y: number): boolean {
  return (
    x >= UTM30_BOUNDS.minX &&
    x <= UTM30_BOUNDS.maxX &&
    y >= UTM30_BOUNDS.minY &&
    y <= UTM30_BOUNDS.maxY
  );
}

/**
 * Obtiene el tipo de infraestructura correspondiente a una tipología.
 * Útil para conversiones aisladas.
 * 
 * @param tipo - TypologyKey de Step2
 * @returns InfrastructureType correspondiente
 */
export function getInfrastructureType(tipo: TypologyKey): InfrastructureType {
  return TYPOLOGY_TO_INFRASTRUCTURE[tipo] ?? InfrastructureType.GENERIC;
}

/**
 * Verifica si una infraestructura tiene coordenadas válidas (no null, no NaN).
 * No valida rangos geográficos, solo existencia de valores numéricos.
 * 
 * @param infra - Infraestructura a verificar
 * @returns true si tiene coordenadas numéricas válidas
 */
export function hasValidCoordinates(infra: Infraestructura): boolean {
  return (
    infra.x !== null &&
    infra.y !== null &&
    !Number.isNaN(infra.x) &&
    !Number.isNaN(infra.y)
  );
}

/**
 * Convierte un id de string (NormalizedInfrastructure) a number (Infraestructura).
 * Útil para sincronizar selección entre MapViewer y Step2.
 * 
 * @param id - ID en formato string
 * @returns ID como number o null si no es válido
 */
export function parseOriginalId(id: string): number | null {
  if (!id || id.trim() === '') {
    return null;
  }
  const parsed = parseInt(id, 10);
  return Number.isNaN(parsed) ? null : parsed;
}
